import { Button, Container, Flex } from '@everlywell/ui-kit';
import StickyFooter from 'components/StickyFooter';
import { usePrePurchaseQuizContext } from 'contexts/PrePurchaseQuiz/PrePurchaseQuizContext';
import React from 'react';
import {
  DeepPartial,
  FieldValues,
  FormProvider,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
} from 'react-hook-form';

import BmiResult from '../Bmi/BmiResult';
import YouAreEligible, {
  PrePurchaseQuizEmailData,
} from '../EligibilityScreens/YouAreEligible';
import YouAreNotEligible from '../EligibilityScreens/YouAreNotEligible';
import FormTitle from '../FormFields/FormTitle';
import OptionSelectorForm, {
  buildOptionSelectOver30BMI_1,
  buildOptionSelectOver30BMI_2,
  buildOptionSelectOver27BMI,
} from '../FormFields/OptionSelectForm';
import PersonalDataForm, {
  PrePurchaseQuizPersonalData,
} from '../FormFields/PersonalDataForm';
import WeightDataForm, {
  PrePurchaseQuizWeightData,
} from '../FormFields/WeightDataForm';
import InitialQuizScreen from '../InitialQuizScreen';

interface PrePurchaseQuizModalStepProps<T extends FieldValues> {
  mainHeading?: string;
  secondaryHeading?: string;
  defaultValues: UnpackNestedValue<DeepPartial<T>>;
  onSubmit: SubmitHandler<T>;
  FormComponent: React.ComponentType;
  buttonText?: string;
  showNextButton?: boolean;
}

export function PrePurchaseQuizModalInitialScreen() {
  return (
    <Flex backgroundColor="eggplant.base" justifyContent="center">
      <Container size={['fluid', null, null, 'xl']}>
        <InitialQuizScreen />
      </Container>
    </Flex>
  );
}

/**
 * BMI Values used as user threshold to show different paths in the quiz.
 */
export const BMI_VALUES = {
  SLIGHTLY_ABOVE: 27,
  OVERWEIGHT: 30,
};

/**
 * PrePurchaseQuizModalStep Component
 *
 * This component renders a form step within a pre-purchase quiz modal. It utilizes React Hook Form for form state management.
 *
 * @template T - The type of the form values.
 *
 * @param {PrePurchaseQuizModalStepProps<T>} props - The props for the component.
 * @param {string} props.mainHeading - The main heading text for the form step.
 * @param {string} props.secondaryHeading - The secondary heading text for the form step.
 * @param {T} props.defaultValues - The default values for the form fields.
 * @param {function} props.onSubmit - The function to call when the form is submitted.
 * @param {React.ComponentType} props.FormComponent - The form component to render within the form step.
 *
 * @returns {JSX.Element} The rendered form step component.
 */
function PrePurchaseQuizModalStep<T extends FieldValues>({
  mainHeading,
  secondaryHeading,
  defaultValues,
  onSubmit,
  FormComponent,
  buttonText,
  showNextButton = true,
}: PrePurchaseQuizModalStepProps<T>): JSX.Element {
  const formMethods = useForm<T>({
    defaultValues,
    mode: 'onBlur',
  });

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} noValidate>
        <FormTitle
          mainHeading={mainHeading || ''}
          secondaryHeading={secondaryHeading || ''}
        />
        <FormComponent />
        {showNextButton && (
          <StickyFooter type="fixed">
            <Container
              size={['sm', 'sm', 'fluid']}
              paddingTop="6"
              paddingBottom={[6, null, 0]}
            >
              <Button width={['100%', null, 'fit-content']} type="submit">
                {buttonText || 'Next'}
              </Button>
            </Container>
          </StickyFooter>
        )}
      </form>
    </FormProvider>
  );
}

export function PrePurchaseQuizModalFirstStep(): JSX.Element {
  const { nextStep, setPersonalData, personalData } =
    usePrePurchaseQuizContext();

  const handleSubmit: SubmitHandler<PrePurchaseQuizPersonalData> = (
    formData,
  ) => {
    setPersonalData(formData);
    nextStep();
  };

  return (
    <PrePurchaseQuizModalStep
      mainHeading="To start..."
      secondaryHeading="Let’s get to know each other"
      defaultValues={personalData || {}}
      onSubmit={handleSubmit}
      FormComponent={PersonalDataForm}
    />
  );
}

export function PrePurchaseQuizModalSecondStep() {
  const { nextStep, setWeightData, weightData } = usePrePurchaseQuizContext();

  const handleSubmit: SubmitHandler<PrePurchaseQuizWeightData> = (formData) => {
    setWeightData(formData);
    nextStep();
  };

  return (
    <PrePurchaseQuizModalStep
      mainHeading="Next up..."
      secondaryHeading="Tell us where you’re starting from"
      defaultValues={weightData || {}}
      onSubmit={handleSubmit}
      FormComponent={WeightDataForm}
    />
  );
}

export function PrePurchaseQuizModalThirdStep() {
  const { bmi, setIsAnswerIneligible } = usePrePurchaseQuizContext();

  if (bmi === undefined) {
    return null;
  }

  if (bmi <= BMI_VALUES.SLIGHTLY_ABOVE) {
    setIsAnswerIneligible(true);
    return null;
  }

  if (bmi > BMI_VALUES.SLIGHTLY_ABOVE) {
    return <BmiResult bmi={bmi} />;
  }

  return null;
}

export function prePurchaseQuizModalOptionSelections(): JSX.Element[] {
  // Array of option selection steps
  const options = [
    buildOptionSelectOver27BMI(),
    buildOptionSelectOver30BMI_1(),
    buildOptionSelectOver30BMI_2(),
  ];

  return options.map((option, index: number) => (
    <OptionSelectorForm key={`option-select-form-${index}`} {...option} />
  ));
}

export function PrePurchaseQuizModalConfirmationStep() {
  const { setEmailData, emailData, personalData } = usePrePurchaseQuizContext();

  const handleSubmit: SubmitHandler<PrePurchaseQuizEmailData> = (formData) => {
    setEmailData(formData);
  };

  return (
    <Flex backgroundColor="eggplant.base" justifyContent="center">
      <Container size={['fluid', null, null, 'xl']}>
        <PrePurchaseQuizModalStep
          defaultValues={emailData || {}}
          onSubmit={handleSubmit}
          FormComponent={() => (
            <YouAreEligible
              firstName={personalData?.personalData.firstName || ''}
            />
          )}
          showNextButton={false}
        />
      </Container>
    </Flex>
  );
}

export const PrePurchaseQuizModalYouAreNotEligibleStep = () => {
  const { personalData } = usePrePurchaseQuizContext();
  const firstName = personalData?.personalData?.firstName || '';

  return (
    <Flex backgroundColor="sky.dark" justifyContent="center">
      <Container size={['fluid', null, null, 'xl']}>
        <YouAreNotEligible firstName={firstName} />
      </Container>
    </Flex>
  );
};
